import { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  GlobalStyles,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import CloseIcon from "@mui/icons-material/Close";
import { TECHNO_COLORS, technoSurfaces, useThemeMode } from "../../theme";
import { createTechnoBeat, TECHNO_BPM } from "./technoBeat";
import { technoGridMotionSelectors, technoGridStyles } from "./gridStyles";
import { Sparkles, technoSparkleStyles } from "./sparkles";

const BEAT = `${60 / TECHNO_BPM}s`;
const { cyan, magenta, violet, acid } = TECHNO_COLORS;

// Animations whose timing means "the beat": these are locked to the audio's downbeat. The slow
// ambient ones (club lights, disco spots, logo) are left to run free.
const BEAT_SYNCED_ANIMATIONS = new Set([
  "techno-beat-magenta",
  "techno-beat-cyan",
  "techno-row-wave",
  "techno-dot",
  "techno-chip",
  "techno-wave-amp",
  "techno-wave-left",
  "techno-wave-right"
]);
const SYNC_TOLERANCE_MS = 15;

// The legend waveform: one sine period is this many px wide.
const WAVE_PERIOD = 22;

/** A sine across four periods, centred in an 18px tall box, as an SVG path. */
const wavePath = (amplitude: number) => {
  const points: string[] = [];
  for (let x = 0; x <= WAVE_PERIOD * 4; x += 1) {
    const y = 9 + amplitude * Math.sin((x / WAVE_PERIOD) * 2 * Math.PI);
    points.push(`${x === 0 ? "M" : "L"}${x} ${y.toFixed(2)}`);
  }
  return points.join(" ");
};

/**
 * Techno mode chrome. The colours come from technoTheme; this adds what a palette cannot: the
 * card glow punching on every beat, the drifting club lights behind the page, the disco-ball
 * light spots over it, and the pill to switch sound on or leave.
 *
 * The shell's own dark colours are set in sx, so they are overridden here through the
 * ma-topbar / ma-nav-rail / ma-menubar / ma-content class names with a body-prefixed selector
 * that outranks the sx class.
 *
 * No full-screen flashing on purpose (photosensitivity), and everything holds still under
 * prefers-reduced-motion.
 */
const technoGlobalStyles = {
  "@keyframes techno-beat-magenta": {
    "0%": {
      boxShadow: `0 0 0 1px ${magenta}, 0 0 26px -2px ${magenta}`
    },
    "100%": {
      boxShadow: `0 0 0 1px rgba(255, 43, 214, 0.3), 0 0 10px -6px ${magenta}`
    }
  },
  "@keyframes techno-beat-cyan": {
    "0%": {
      boxShadow: `0 0 0 1px ${cyan}, 0 0 26px -2px ${cyan}`
    },
    "100%": {
      boxShadow: `0 0 0 1px rgba(0, 240, 255, 0.3), 0 0 10px -6px ${cyan}`
    }
  },
  "@keyframes techno-drift": {
    "0%": { transform: "translate(0, 0) scale(1)" },
    "100%": { transform: "translate(8vw, 6vh) scale(1.2)" }
  },
  "@keyframes techno-spin": {
    "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
    "100%": { transform: "translate(-50%, -50%) rotate(360deg)" }
  },
  "@keyframes techno-ball": {
    "0%": { backgroundPosition: "0 0, 0 0, 0 0, 0 0" },
    "100%": { backgroundPosition: "0 0, 0 0, 12px 0, 0 0" }
  },
  "@keyframes techno-wave-left": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: `translateX(-${WAVE_PERIOD}px)` }
  },
  "@keyframes techno-wave-right": {
    "0%": { transform: `translateX(-${WAVE_PERIOD}px)` },
    "100%": { transform: "translateX(0)" }
  },
  "@keyframes techno-wave-amp": {
    "0%": { transform: "scaleY(1)" },
    "100%": { transform: "scaleY(0.45)" }
  },
  "body .ma-topbar": {
    background: `linear-gradient(90deg, ${technoSurfaces.chrome}, #1c0736 50%, ${technoSurfaces.chrome})`,
    borderBottom: `1px solid ${magenta}`,
    boxShadow: `0 0 18px rgba(255, 43, 214, 0.45)`
  },
  "body .ma-nav-rail, body .MuiDrawer-paper": {
    backgroundColor: technoSurfaces.chrome,
    borderRight: `1px solid ${cyan}`,
    boxShadow: `0 0 18px rgba(0, 240, 255, 0.3)`
  },
  "body .ma-nav-rail .Mui-selected svg, body .MuiDrawer-paper .Mui-selected svg":
    {
      fill: magenta,
      filter: `drop-shadow(0 0 6px ${magenta})`
    },
  "body .ma-menubar": {
    backgroundColor: "rgba(18, 7, 37, 0.72)",
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid rgba(0, 240, 255, 0.35)`
  },
  "body .ma-content": {
    backgroundColor: "transparent"
  },
  "body .MuiCard-root": {
    animation: `techno-beat-magenta ${BEAT} ease-out infinite`
  },
  "body .MuiCard-root:nth-of-type(even)": {
    animationName: "techno-beat-cyan"
  },
  "body .MuiTypography-h4, body .MuiTypography-h5, body .MuiTypography-h6": {
    textShadow: `0 0 14px rgba(0, 240, 255, 0.55), 0 0 2px rgba(255, 255, 255, 0.6)`
  },
  "body .MuiButton-containedPrimary": {
    backgroundImage: `linear-gradient(90deg, ${cyan}, ${magenta})`,
    boxShadow: `0 0 16px rgba(255, 43, 214, 0.55)`
  },
  ...technoGridStyles,
  ...technoSparkleStyles,
  "@media (prefers-reduced-motion: reduce)": {
    [`body .MuiCard-root, body .techno-motion, body .techno-motion *, ${technoGridMotionSelectors}`]:
      {
        animation: "none !important"
      }
  }
};

/** The club lights: slow drifting colour behind everything, on the page's own canvas. */
const Backdrop: FC = () => (
  <Box
    aria-hidden
    className="techno-motion"
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: -1,
      pointerEvents: "none",
      overflow: "hidden",
      backgroundColor: technoSurfaces.canvas,
      "& > div": {
        position: "absolute",
        borderRadius: "50%",
        filter: "blur(40px)",
        animation: "techno-drift 14s ease-in-out infinite alternate"
      }
    }}
  >
    <Box
      sx={{
        width: "60vmax",
        height: "60vmax",
        top: "-20vmax",
        left: "-15vmax",
        background: `radial-gradient(circle, rgba(255, 43, 214, 0.35), transparent 65%)`
      }}
    />
    <Box
      sx={{
        width: "55vmax",
        height: "55vmax",
        top: "-10vmax",
        right: "-20vmax",
        background: `radial-gradient(circle, rgba(0, 240, 255, 0.28), transparent 65%)`,
        animationDelay: "-5s !important",
        animationDuration: "18s !important"
      }}
    />
    <Box
      sx={{
        width: "80vmax",
        height: "50vmax",
        bottom: "-25vmax",
        left: "10vw",
        background: `radial-gradient(circle, rgba(139, 61, 255, 0.4), transparent 65%)`,
        animationDelay: "-9s !important",
        animationDuration: "22s !important"
      }}
    />
  </Box>
);

/**
 * Disco-ball reflections: three grids of small light spots rotating slowly over the page.
 * Screen-blended at low opacity so text underneath stays readable, and below the modal layer
 * so dialogs are not covered.
 */
const LightSpots: FC = () => (
  <Box
    aria-hidden
    className="techno-motion"
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 1250,
      pointerEvents: "none",
      overflow: "hidden",
      mixBlendMode: "screen",
      opacity: 0.24
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "250vmax",
        height: "250vmax",
        backgroundImage: [
          `radial-gradient(circle, rgba(255, 255, 255, 0.95) 0 1.5px, transparent 2.5px)`,
          `radial-gradient(circle, ${cyan} 0 2px, transparent 3px)`,
          `radial-gradient(circle, ${magenta} 0 2px, transparent 3px)`,
          `radial-gradient(circle, ${acid} 0 1.5px, transparent 2.5px)`
        ].join(", "),
        backgroundSize: "53px 53px, 89px 89px, 127px 127px, 173px 173px",
        backgroundPosition: "0 0, 20px 31px, 57px 11px, 90px 70px",
        animation: "techno-spin 60s linear infinite"
      }}
    />
  </Box>
);

/** A small mirror ball: faceted tiles sliding sideways read as a spin. */
const MirrorBall: FC = () => (
  <Box
    aria-hidden
    className="techno-motion"
    sx={{
      width: 26,
      height: 26,
      flexShrink: 0,
      borderRadius: "50%",
      backgroundImage: [
        `radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.95) 0 8%, transparent 26%)`,
        `repeating-linear-gradient(0deg, rgba(20, 0, 40, 0.45) 0 1px, transparent 1px 5px)`,
        `repeating-linear-gradient(90deg, rgba(20, 0, 40, 0.45) 0 1px, transparent 1px 6px)`,
        `linear-gradient(135deg, #f4f4ff, #9a90b8 55%, #f4f4ff)`
      ].join(", "),
      boxShadow: `0 0 12px rgba(255, 255, 255, 0.5), 0 0 26px ${violet}`,
      animation: "techno-ball 0.8s linear infinite"
    }}
  />
);

/**
 * An oscilloscope-style waveform: two sine waves scrolling in opposite directions (so they
 * interfere and never look static), the whole trace punching taller on each kick. Each path
 * is four periods wide inside a two-period window and scrolls exactly one period per loop,
 * so the loop is seamless.
 */
const Waveform: FC = () => (
  <Box
    aria-hidden
    className="techno-motion"
    sx={{
      position: "relative",
      width: WAVE_PERIOD * 2,
      height: 18,
      overflow: "hidden",
      animation: `techno-wave-amp ${BEAT} ease-out infinite`,
      "& svg": {
        position: "absolute",
        top: 0,
        left: 0,
        width: WAVE_PERIOD * 4,
        height: 18,
        overflow: "visible"
      }
    }}
  >
    <svg
      viewBox={`0 0 ${WAVE_PERIOD * 4} 18`}
      style={{
        animation: `techno-wave-left ${BEAT} linear infinite`,
        filter: `drop-shadow(0 0 3px ${cyan})`
      }}
    >
      <defs>
        <linearGradient id="techno-wave-gradient" x1="0" x2="1">
          <stop offset="0%" stopColor={cyan} />
          <stop offset="50%" stopColor={magenta} />
          <stop offset="100%" stopColor={cyan} />
        </linearGradient>
      </defs>
      <path
        d={wavePath(6)}
        fill="none"
        stroke="url(#techno-wave-gradient)"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
    <svg
      viewBox={`0 0 ${WAVE_PERIOD * 4} 18`}
      style={{
        animation: `techno-wave-right calc(${BEAT} * 2) linear infinite`,
        opacity: 0.75
      }}
    >
      <path
        d={wavePath(4)}
        fill="none"
        stroke={acid}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

export const TechnoMode: FC = () => {
  const { techno, toggleTechno } = useThemeMode();
  const beat = useRef(createTechnoBeat());
  const [soundOn, setSoundOn] = useState(false);

  // Leaving techno mode, or unmounting, always silences the beat.
  useEffect(() => {
    const current = beat.current;
    if (!techno) {
      current.stop();
      setSoundOn(false);
    }
    return () => current.stop();
  }, [techno]);

  // Lock the beat-driven CSS animations to the kick. Each CSS animation otherwise starts its
  // own clock when its element mounts (and grid rows remount as you scroll), so they drift off
  // the audio. Every half second, any beat animation whose start time is off is moved onto
  // the audio's first downbeat (or, while silent, onto when techno mode switched on, so the
  // visuals at least agree with each other). Only the start time moves, so each row keeps its
  // wave offset.
  useEffect(() => {
    if (!techno) return;
    const silentOrigin = performance.now();
    const sync = () => {
      const origin = beat.current.downbeatMs() ?? silentOrigin;
      document.getAnimations().forEach((animation) => {
        if (
          !(animation instanceof CSSAnimation) ||
          !BEAT_SYNCED_ANIMATIONS.has(animation.animationName)
        ) {
          return;
        }
        const start = Number(animation.startTime ?? NaN);
        // The audio clock is quantised to render blocks, so ignore a few ms of jitter rather
        // than nudging every animation on every pass.
        if (!(Math.abs(start - origin) < SYNC_TOLERANCE_MS)) {
          animation.startTime = origin;
        }
      });
    };
    sync();
    const timer = setInterval(sync, 500);
    return () => clearInterval(timer);
  }, [techno]);

  if (!techno) return null;

  const toggleSound = () => {
    if (soundOn) beat.current.stop();
    else beat.current.start();
    setSoundOn(!soundOn);
  };

  return (
    <>
      <GlobalStyles styles={technoGlobalStyles} />
      <Backdrop />
      <LightSpots />
      <Sparkles />
      <Box
        role="status"
        sx={{
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1260,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          paddingLeft: 1.5,
          paddingRight: 0.5,
          paddingY: 0.5,
          borderRadius: 999,
          backgroundColor: "rgba(23, 10, 44, 0.85)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${magenta}`,
          boxShadow: `0 0 20px rgba(255, 43, 214, 0.5), inset 0 0 12px rgba(0, 240, 255, 0.15)`,
          whiteSpace: "nowrap"
        }}
      >
        <MirrorBall />
        <Typography variant="subtitle2" sx={{ color: "#F7F0FF" }}>
          Techno mode
        </Typography>
        <Waveform />
        <Typography variant="caption" sx={{ color: cyan }}>
          {TECHNO_BPM} BPM
        </Typography>
        <Tooltip title={soundOn ? "Mute" : "Play the beat"}>
          <IconButton
            size="small"
            onClick={toggleSound}
            aria-label={soundOn ? "Mute" : "Play the beat"}
            sx={{ color: soundOn ? acid : "#F7F0FF" }}
          >
            {soundOn ? (
              <VolumeUpIcon fontSize="small" />
            ) : (
              <VolumeOffIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Leave techno mode">
          <IconButton
            size="small"
            onClick={toggleTechno}
            aria-label="Leave techno mode"
            sx={{ color: "#F7F0FF" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};
